import { Box, Skeleton, Stack } from '@mui/material';

interface TwoPieChartsSkeletonProps {
  pieWidth?: number;
  pieHeight?: number;
  circleDiameter?: number;
  legendItems?: number;
  variant?: 'assetAllocation' | 'balanceSheet';
  /**
   * Force the skeleton to render in the mobile/stacked layout regardless of viewport.
   * Useful for narrow dashboard widgets where container width (not viewport) drives layout.
   */
  forceMobile?: boolean;
}

export default function TwoPieChartsSkeleton({
  pieWidth,
  pieHeight,
  circleDiameter,
  legendItems = 3,
  variant = 'assetAllocation',
  forceMobile = false,
}: TwoPieChartsSkeletonProps) {
  const effectivePieWidth = pieWidth ?? (forceMobile ? 300 : 450);
  const effectivePieHeight = pieHeight ?? (forceMobile ? 350 : 300);
  const effectiveCircleDiameter = circleDiameter ?? (forceMobile ? 220 : 240);

  const Column = () => (
    <Box
      textAlign='center'
      width='100%'
      maxWidth='500px'
    >
      {variant === 'assetAllocation' ? (
        <>
          <Skeleton
            variant='text'
            width='60%'
            height={32}
            sx={{ mx: 'auto', mb: 1 }}
          />
          <Skeleton
            variant='text'
            width='40%'
            height={24}
            sx={{ mx: 'auto' }}
          />
          <Skeleton
            variant='text'
            width='50%'
            height={24}
            sx={{ mx: 'auto', mb: 2 }}
          />
        </>
      ) : (
        <>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
            width='100%'
          >
            <Skeleton
              variant='text'
              width='40%'
              height={28}
            />
            <Skeleton
              variant='text'
              width='30%'
              height={28}
            />
          </Box>
        </>
      )}

      <Box
        position='relative'
        height={effectivePieHeight}
        width={effectivePieWidth}
        sx={{ mx: 'auto' }}
      >
        <Skeleton
          variant='circular'
          width={effectiveCircleDiameter}
          height={effectiveCircleDiameter}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      <Stack
        direction='column'
        spacing={1}
        justifyContent='center'
        alignItems='center'
        mt={2}
      >
        {[...Array(legendItems)].map((_, i) => (
          <Stack
            key={i}
            direction='row'
            spacing={1}
          >
            <Skeleton
              variant='text'
              width={250}
            />
          </Stack>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Stack
      direction='column'
      gap={4}
      alignItems='center'
    >
      <Stack
        direction={forceMobile ? 'column' : { xs: 'column', md: 'row' }}
        gap={forceMobile ? 4 : { xs: 4, md: 8 }}
        alignItems='center'
        justifyContent='center'
      >
        <Column />
        <Column />
      </Stack>
    </Stack>
  );
}
